import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import startsWith from 'lodash/startsWith';
import toString from 'lodash/toString';
import transformRelativePath from './transformRelativePath';

const updateFieldValue = (
  field,
  changeValue,
  actionName,
  actionValue,
  formik,
  formValues
) => {
  // the reason for using setTimeout 0ms  //https://github.com/jaredpalmer/formik/issues/930
  if (changeValue === '$$EXCLUDE') {
    setTimeout(() => formik.setFieldValue(field), 0);
  } else if (changeValue === '$$OBSERVED') {
    setTimeout(() => formik.setFieldValue(field, actionValue), 0);
  } else if (startsWith(changeValue, '$$PARENT')) {
    const transformedField = transformRelativePath(actionName, changeValue);
    const value = get(formValues, transformedField);

    if (!isUndefined(value)) {
      setTimeout(() => formik.setFieldValue(field, value), 0);
    }
  } else if (startsWith(changeValue, '&&')) {
    const value = get(formValues, changeValue.slice(2));

    if (!isUndefined(value)) {
      setTimeout(() => formik.setFieldValue(field, value), 0);
    }
  } else {
    setTimeout(() => formik.setFieldValue(field, changeValue), 0);
  }
};

const findChangeFieldCollection = (parts, formValues) => {
  let newParts = [];
  const [part1, part2] = parts;
  const total = get(formValues, part1.slice(0, -1), []).length;

  for (let idx = 0; idx < total; idx++) {
    let path = `${part1}${idx}${part2}`;
    let checkPath = `${part1.slice(0, -1)}[${idx}]${part2.slice(1)}`;

    const exists = get(
      formValues,
      parts.length > 2 ? checkPath.slice(0, -1) : checkPath,
      undefined
    );

    if (!isUndefined(exists)) {
      if (parts.length > 2) {
        newParts = newParts.concat(
          findChangeFieldCollection([path, ...parts.slice(2)], formValues)
        );
      } else {
        newParts.push(path);
      }
    }
  }

  return newParts;
};

const handleChangeField = ({
  actionName,
  actionValue,
  changeField,
  changeMap,
  formValues,
  formik,
}) => {
  const allFields =
    changeField.indexOf('$$INDEX') === -1
      ? [changeField]
      : findChangeFieldCollection(changeField.split('$$INDEX'), formValues);

  for (let field of allFields) {
    field = transformRelativePath(actionName, field);

    const allowFieldCreation = field.slice(-1) === '+';

    if (allowFieldCreation) {
      field = field.slice(0, -1);
    }

    if (isUndefined(get(formValues, field)) && !allowFieldCreation) {
      continue;
    }

    let isUpdated = false;

    for (const observedValue of Object.keys(changeMap)) {
      const changeValue = changeMap[observedValue];

      if (observedValue === toString(actionValue)) {
        updateFieldValue(
          field,
          changeValue,
          actionName,
          actionValue,
          formik,
          formValues
        );

        isUpdated = true;
        break;
      } else if (startsWith(observedValue, '&&')) {
        const value = get(formValues, observedValue.slice(2));

        if (!isUndefined(value) && value === toString(actionValue)) {
          updateFieldValue(
            field,
            changeValue,
            actionName,
            actionValue,
            formik,
            formValues
          );

          isUpdated = true;
          break;
        }
      } else if (startsWith(observedValue, '$$PARENT')) {
        const transformedField = transformRelativePath(
          actionName,
          observedValue
        );

        const value = get(formValues, transformedField);

        if (!isUndefined(value) && value === toString(actionValue)) {
          updateFieldValue(
            field,
            changeValue,
            actionName,
            actionValue,
            formik,
            formValues
          );

          isUpdated = true;
          break;
        }
      }
    }

    const changeDefaultValue = changeMap['$$DEFAULT'];

    if (!isUpdated && !isUndefined(changeDefaultValue)) {
      updateFieldValue(
        field,
        changeDefaultValue,
        actionName,
        actionValue,
        formik,
        formValues
      );
    }
  }
};

const handleObservedField = ({
  actionName,
  actionValue,
  changeMap,
  formValues,
  formik,
  observedField,
}) => {
  const pattern = new RegExp(
    `^${observedField
      .replace(/\./g, '\\.')
      .replace(/\$\$INDEX/g, '\\d+')
      .replace(/%/g, '[A-Za-z0-9.]*')}$`
  );

  const isChanged = pattern.test(actionName);

  if (isChanged) {
    Object.keys(changeMap).forEach(changeFields =>
      changeFields.split(';').forEach(changeField =>
        handleChangeField({
          actionName,
          actionValue,
          changeMap: changeMap[changeFields],
          changeField,
          formik,
          formValues,
        })
      )
    );
  }
};

const parseRules = ({ actionName, actionValue, data, formValues, formik }) =>
  Object.keys(data).forEach(observedFields => {
    const changeMap = data[observedFields];

    observedFields.split(';').forEach(observedField =>
      handleObservedField({
        actionName,
        actionValue,
        changeMap,
        observedField,
        formik,
        formValues,
      })
    );
  });

const handleChanges = ({
  action,
  formValues,
  formik,
  onAdd,
  onChange,
  onRemove,
}) => {
  if (onChange && action.type === 'onChange') {
    parseRules({
      actionName: action.name,
      actionValue: action.value,
      formik,
      formValues,
      data: onChange,
    });
  }

  if (onAdd && action.type === 'onAdd') {
    parseRules({
      actionName: action.name,
      actionValue: action.index,
      formik,
      formValues,
      data: onAdd,
    });
  }

  if (onRemove && action.type === 'onRemove') {
    parseRules({
      actionName: action.name,
      actionValue: action.index,
      formik,
      formValues,
      data: onRemove,
    });
  }
};

export default handleChanges;
